import {
  AlertBanner,
  Button,
  Chip,
  Icon,
  Illustration,
  InputField,
  InlineMessage,
  Typography,
} from "@saviynt/design-system";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CertificateSection from "./CertificateSection";
import EnableSsoButton from "./EnableSsoButton";
import TestConnectionButton from "./TestConnectionButton";
import type { SsoTestResult } from "../hooks/useTestConnection";
import "./SsoCreatePage.css";

interface SpMetadata {
  entityId: string;
  acsUrl: string;
  sloUrl?: string | null;
}

interface SsoConfigFormProps {
  /** Page title displayed at the top */
  title: React.ReactNode;
  /** Initial value for display name field */
  initialDisplayName?: string;
  /** Initial value for entity descriptor URL field */
  initialEntityDescriptorUrl?: string;
  /** Initial value for email attribute field */
  initialEmailAttribute?: string;
  /** SP metadata for read-only fields */
  spMetadata?: SpMetadata | null;
  /** Whether the save action is in progress */
  isPending: boolean;
  /** General error message to display at the top */
  error?: string | null;
  /** Field-specific validation errors from the API */
  fieldErrors?: Record<string, string> | null;
  /** Called when user clicks Save with validated values */
  onSave: (values: {
    displayName: string;
    entityDescriptorUrl: string;
    emailAttribute: string;
  }) => void;
  /** Called when user clicks Back */
  onBack: () => void;
  /** SSO config data for certificate section (available after save) */
  ssoConfig?: {
    status: "TEST" | "ENABLED" | "DISABLED";
    idpName?: string | null;
    signingCert?: string | null;
    encryptionCert?: string | null;
  } | null;
  /** Whether the SSO config is currently loading/refetching */
  isConfigLoading?: boolean;
  /** Success message to display at the top of the form */
  successMessage?: string | null;
  /** Called when user clicks Enable SSO */
  onEnableSso?: () => void;
  /** Whether the enable SSO action is in progress */
  isEnabling?: boolean;
  /** Error message for the enable SSO action */
  enableError?: string | null;
  /** Error message when config refetch fails after save */
  configFetchError?: string | null;
  /** Callback to retry fetching the config */
  onRetryConfigFetch?: () => void;
}

const EMAIL_ATTRIBUTE_DEFAULT =
  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress";

export default function SsoConfigForm({
  title,
  initialDisplayName = "",
  initialEntityDescriptorUrl = "",
  initialEmailAttribute = EMAIL_ATTRIBUTE_DEFAULT,
  spMetadata,
  isPending,
  error,
  fieldErrors,
  onSave,
  onBack,
  ssoConfig,
  isConfigLoading,
  successMessage,
  onEnableSso,
  isEnabling,
  enableError,
  configFetchError,
  onRetryConfigFetch,
}: SsoConfigFormProps) {
  const intl = useIntl();
  const errorRef = useRef<HTMLDivElement>(null);

  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [entityDescriptorUrl, setEntityDescriptorUrl] = useState(
    initialEntityDescriptorUrl,
  );
  const [emailAttribute, setEmailAttribute] = useState(initialEmailAttribute);
  const [isCriticalDisplayName, setIsCriticalDisplayName] = useState(false);
  const [isCriticalUrl, setIsCriticalUrl] = useState(false);
  // Used for EnableSsoButton highlight after successful test
  const [testSucceeded, setTestSucceeded] = useState(false);
  // Banner dismiss state
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  // Track whether form has been modified from initial values
  const isDirty =
    displayName.trim() !== initialDisplayName.trim() ||
    entityDescriptorUrl.trim() !== initialEntityDescriptorUrl.trim() ||
    emailAttribute.trim() !== initialEmailAttribute.trim();

  // Reset dismiss state when error changes (new error should re-show banner)
  useEffect(() => {
    setIsBannerDismissed(false);
  }, [error]);

  // Mark fields as critical when field errors come from API
  useEffect(() => {
    if (fieldErrors?.metadataUrl) {
      setIsCriticalUrl(true);
    }
    if (fieldErrors?.displayName) {
      setIsCriticalDisplayName(true);
    }
  }, [fieldErrors]);

  // Scroll to error message when it appears
  useEffect(() => {
    if (error && !isBannerDismissed && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      errorRef.current.focus();
    }
  }, [error, isBannerDismissed]);

  const handleSave = () => {
    // Auto-dismiss banner on Save click
    setIsBannerDismissed(true);

    let hasError = false;

    if (!displayName.trim()) {
      setIsCriticalDisplayName(true);
      hasError = true;
    }
    if (!entityDescriptorUrl.trim()) {
      setIsCriticalUrl(true);
      hasError = true;
    }

    if (hasError) return;

    onSave({
      displayName: displayName.trim(),
      entityDescriptorUrl: entityDescriptorUrl.trim(),
      emailAttribute: emailAttribute.trim(),
    });
  };

  const handleTestResult = (result: SsoTestResult) => {
    if (result.success) {
      setTestSucceeded(true);
    }
  };

  const handleCopy = async (value: string) => {
    await navigator.clipboard.writeText(value);
  };

  // Wrap setValue to auto-dismiss banner on form interaction
  const handleSetDisplayName = (value: string) => {
    setDisplayName(value);
    setIsBannerDismissed(true);
  };

  const handleSetEntityDescriptorUrl = (value: string) => {
    setEntityDescriptorUrl(value);
    setIsBannerDismissed(true);
  };

  const handleSetEmailAttribute = (value: string) => {
    setEmailAttribute(value);
    setIsBannerDismissed(true);
  };

  return (
    <div className="SsoCreate">
      <div className="SsoCreate__content">
        {/* Page Title */}
        <Typography kind="h1" color="--color-foreground-neutral-bold">
          {title}
        </Typography>

        {/* Error banner */}
        {error && !isBannerDismissed && (
          <div ref={errorRef} tabIndex={-1} aria-live="polite" role="alert">
            <AlertBanner
              colorTheme="Critical"
              title={error}
              isFloating={false}
              onCancel={() => setIsBannerDismissed(true)}
              isColonVisible={false}
            />
          </div>
        )}

        {/* Success message */}
        {successMessage && (
          <div aria-live="polite" role="status">
            <InlineMessage colorTheme="success" text={successMessage} />
          </div>
        )}

        {/* Form Fields */}
        <div className="SsoCreate__section">
          <div className="SsoCreate__fieldWrapper">
            <InputField
              name="displayName"
              label={
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="Vnhyq7"
                    defaultMessage="Display Name"
                    description="Label for SSO display name field"
                  />
                </Typography>
              }
              placeholder={intl.formatMessage({
                id: "rM+uBl",
                defaultMessage: "Placeholder",
                description: "Display name placeholder",
              })}
              value={displayName}
              setValue={handleSetDisplayName}
              isCritical={isCriticalDisplayName}
              setIsCritical={setIsCriticalDisplayName}
              size="large"
            />
          </div>

          <div className="SsoCreate__fieldWrapper">
            <InputField
              name="spEntityId"
              label={
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="3BBRVe"
                    defaultMessage="SP Entity ID"
                    description="Label for SP Entity ID field"
                  />
                </Typography>
              }
              placeholder=""
              value={spMetadata?.entityId ?? ""}
              isReadOnly
              readOnlyKind="boxed"
              size="large"
              suffixButtonType="password"
              suffixButtonIcon={
                <Icon
                  kind="Copy"
                  size="small"
                  color="--color-foreground-neutral-bold"
                />
              }
              suffixOnClick={() => handleCopy(spMetadata?.entityId ?? "")}
              suffixButtonIconLabel={intl.formatMessage({
                id: "8+W/za",
                defaultMessage: "Copy SP Entity ID",
                description: "Copy SP Entity ID button",
              })}
            />
          </div>

          <div className="SsoCreate__fieldWrapper">
            <InputField
              name="redirectUrl"
              label={
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="u8x19h"
                    defaultMessage="Redirect URL"
                    description="Label for Redirect URL field"
                  />
                </Typography>
              }
              placeholder=""
              value={spMetadata?.acsUrl ?? ""}
              isReadOnly
              readOnlyKind="boxed"
              size="large"
              suffixButtonType="password"
              suffixButtonIcon={
                <Icon
                  kind="Copy"
                  size="small"
                  color="--color-foreground-neutral-bold"
                />
              }
              suffixOnClick={() => handleCopy(spMetadata?.acsUrl ?? "")}
              suffixButtonIconLabel={intl.formatMessage({
                id: "Ai1m+k",
                defaultMessage: "Copy Redirect URL",
                description: "Copy Redirect URL button",
              })}
            />
          </div>

          <div className="SsoCreate__fieldWrapper">
            <InputField
              name="logoutUrl"
              label={
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="+sIA8q"
                    defaultMessage="Logout URL"
                    description="Label for Single Logout URL field"
                  />
                </Typography>
              }
              placeholder=""
              value={spMetadata?.sloUrl ?? ""}
              isReadOnly
              readOnlyKind="boxed"
              size="large"
              suffixButtonType="password"
              suffixButtonIcon={
                <Icon
                  kind="Copy"
                  size="small"
                  color="--color-foreground-neutral-bold"
                />
              }
              suffixOnClick={() => handleCopy(spMetadata?.sloUrl ?? "")}
              suffixButtonIconLabel={intl.formatMessage({
                id: "lWYhCI",
                defaultMessage: "Copy Logout URL",
                description: "Copy Logout URL button",
              })}
            />
          </div>

          <div className="SsoCreate__fieldWrapper">
            <InputField
              name="emailAttribute"
              label={
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="1WBL67"
                    defaultMessage="Email Attribute"
                    description="Label for email attribute mapping field"
                  />
                </Typography>
              }
              placeholder={intl.formatMessage({
                id: "CMrYiy",
                defaultMessage:
                  "e.g. http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
                description: "Email attribute placeholder",
              })}
              value={emailAttribute}
              setValue={handleSetEmailAttribute}
              size="large"
            />
          </div>
        </div>

        {/* Security & Attributes */}
        <div className="SsoCreate__section">
          <Typography kind="h3" color="--color-foreground-neutral-bold">
            <FormattedMessage
              id="HV+gbt"
              defaultMessage="Security & Attributes"
              description="Security and attributes section heading"
            />
          </Typography>
          <div className="SsoCreate__chips">
            <Chip
              label={intl.formatMessage({
                id: "6fjw+I",
                defaultMessage: "Assertions Signed",
                description: "Assertions signed chip",
              })}
              kind="outlined"
              size="medium"
            />
            <Chip
              label={intl.formatMessage({
                id: "f4CMxG",
                defaultMessage: "Assertions Encrypted",
                description: "Assertions encrypted chip",
              })}
              kind="outlined"
              size="medium"
            />
            <Chip
              label={intl.formatMessage({
                id: "jg2I+I",
                defaultMessage: "Validate Signatures",
                description: "Validate signatures chip",
              })}
              kind="outlined"
              size="medium"
            />
          </div>
        </div>

        {/* Entity Descriptor Configuration */}
        <div className="SsoCreate__section">
          <Typography kind="h3" color="--color-foreground-neutral-bold">
            <FormattedMessage
              id="NY4tGg"
              defaultMessage="Entity Descriptor Configuration"
              description="Entity descriptor configuration section heading"
            />
          </Typography>

          <div className="SsoCreate__descriptorCard">
            <div className="SsoCreate__descriptorInner">
              <div className="SsoCreate__descriptorQuestion">
                <Typography
                  kind="body2-bold"
                  color="--color-foreground-neutral-bold"
                >
                  <FormattedMessage
                    id="albN9f"
                    defaultMessage="How would you like to configure entity descriptor?"
                    description="Entity descriptor configuration question"
                  />
                </Typography>
                <div className="SsoCreate__radioOption">
                  <Illustration
                    kind="CreateApplication"
                    className="SsoCreate__radioIllustration"
                  />
                  <div className="SsoCreate__radioContent">
                    <Typography
                      kind="h3"
                      color="--color-foreground-secondary-bold"
                    >
                      <FormattedMessage
                        id="DgKse+"
                        defaultMessage="via URL"
                        description="Via URL option title"
                      />
                    </Typography>
                    <Typography
                      kind="body2"
                      color="--color-foreground-neutral-subtle"
                    >
                      <FormattedMessage
                        id="hVztzn"
                        defaultMessage="Fetch entity descriptor information from an entity descriptor URL."
                        description="Via URL option description"
                      />
                    </Typography>
                  </div>
                </div>
              </div>

              <InputField
                name="entityDescriptorUrl"
                label={
                  <Typography
                    kind="body2-bold"
                    color="--color-foreground-neutral-bold"
                  >
                    <FormattedMessage
                      id="Tp125U"
                      defaultMessage="Entity Descriptor URL"
                      description="Label for entity descriptor URL field"
                    />
                  </Typography>
                }
                placeholder={intl.formatMessage({
                  id: "hs6JcD",
                  defaultMessage: "Placeholder",
                  description: "Entity descriptor URL placeholder",
                })}
                value={entityDescriptorUrl}
                setValue={handleSetEntityDescriptorUrl}
                isRequired
                isCritical={isCriticalUrl}
                setIsCritical={setIsCriticalUrl}
                size="large"
                CriticalHelperText={
                  isCriticalUrl && fieldErrors?.metadataUrl ? (
                    <InlineMessage
                      colorTheme="critical"
                      text={fieldErrors.metadataUrl}
                    />
                  ) : undefined
                }
              />
            </div>
          </div>
        </div>

        {/* Certificate Section - shown when ssoConfig has certificates */}
        {ssoConfig &&
          (ssoConfig.signingCert ||
            ssoConfig.encryptionCert ||
            isConfigLoading ||
            configFetchError) && (
            <CertificateSection
              signingCert={ssoConfig.signingCert}
              encryptionCert={ssoConfig.encryptionCert}
              idpName={ssoConfig.idpName ?? ""}
              status={ssoConfig.status}
              isLoading={isConfigLoading}
              fetchError={configFetchError}
              onRetry={onRetryConfigFetch}
            />
          )}

        {/* Config fetch error - shown when ssoConfig is not available but there's a fetch error */}
        {!ssoConfig && configFetchError && (
          <CertificateSection
            signingCert={null}
            encryptionCert={null}
            idpName=""
            status="TEST"
            fetchError={configFetchError}
            onRetry={onRetryConfigFetch}
          />
        )}

        {/* Test Connection - shown when ssoConfig exists and status is TEST or ENABLED */}
        <TestConnectionButton
          isVisible={
            Boolean(ssoConfig) &&
            (ssoConfig?.status === "TEST" || ssoConfig?.status === "ENABLED")
          }
          onTestResult={handleTestResult}
        />

        {/* Enable SSO - shown when ssoConfig exists and status is TEST */}
        {ssoConfig && onEnableSso && (
          <>
            <EnableSsoButton
              status={ssoConfig.status}
              isHighlighted={testSucceeded}
              onEnable={onEnableSso}
              isPending={isEnabling ?? false}
            />
            {enableError && (
              <div aria-live="polite" style={{ marginTop: "8px" }}>
                <InlineMessage
                  colorTheme="critical"
                  text={enableError}
                  size="small"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="SsoCreate__footer">
        <div className="SsoCreate__footerInner">
          <Button kind="subtle" size="medium" onClick={onBack}>
            <FormattedMessage
              id="oo/YC7"
              defaultMessage="Back"
              description="Back button label"
            />
          </Button>
          <Button
            kind="filled"
            size="medium"
            onClick={handleSave}
            isLoading={isPending}
            isDisabled={!isDirty}
          >
            <FormattedMessage
              id="2y24a/"
              defaultMessage="Save"
              description="Save button label"
            />
          </Button>
        </div>
      </div>
    </div>
  );
}
